import React, { useState } from "react";
import { Box, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import {
  useTherapyCandidatesForTherapyRx,
  useSetTherapyRxTherapy,
} from "../../stores/Appointment/therapyMethod.hooks";

/**
 * MD-325 — the "Heilm." cell of "Terminformat für neue Rezepte".
 *
 * Two things happen here.
 *
 * 1. The prescribed TERM is shown. It used to read `therapyRx.therapy`, a field
 *    MD-232 removed when it split prescribed / performed / billed apart, so the
 *    cell had been silently blank ever since — the rows are typed `any`, so
 *    nothing failed and nothing was flagged.
 *
 * 2. When the term is an umbrella covering several applications ("Wärme" →
 *    Heißluft / Warmpackung / Heiße Rolle), the planner can fix which one this
 *    prescription is delivered as, before anything is scheduled. Without that,
 *    the application was decided per appointment by whichever therapist
 *    happened to be booked, so one prescription produced a mixture and the
 *    Zuzahlung moved whenever an appointment moved.
 *
 * The picker is deliberately absent when the term resolves to exactly one line
 * item, which is the overwhelming majority: there is no decision to offer, and
 * a disabled control on every row would be noise.
 */
interface TherapyRxMethodCellProps {
  therapyRxId?: string;
  /** Term label from the row itself, so the cell reads correctly before the
   *  candidates request resolves (and if it fails). */
  fallbackLabel?: string | null;
  disabled?: boolean;
}

const TherapyRxMethodCell: React.FC<TherapyRxMethodCellProps> = ({
  therapyRxId,
  fallbackLabel,
  disabled,
}) => {
  const { data } = useTherapyCandidatesForTherapyRx(therapyRxId);
  const setTherapy = useSetTherapyRxTherapy();
  // The tooltip is controlled so it closes while the menu is open — left to its
  // hover listener it stays up over the options and hides what is being picked.
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const termLabel = data?.termLabel ?? fallbackLabel ?? "—";
  const candidates = data?.candidates ?? [];
  const hasChoice = candidates.length > 1;

  if (!hasChoice) {
    return <Typography variant="inherit">{termLabel}</Typography>;
  }

  // "" is the explicit "no choice recorded" option — it clears the line back to
  // the default priority rule rather than leaving the select in a blank state.
  const value = data?.chosenTherapyId ?? "";
  const defaultAbbreviation = candidates.find((c) => c.isDefault)?.abbreviation;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="inherit">{termLabel}</Typography>
      <Tooltip
        placement="top"
        open={tooltipOpen && !menuOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        title={
          "Welche Anwendung erbracht wird. Gilt für neu geplante Termine dieser " +
          "Verordnungszeile; bereits geplante Termine bleiben unverändert."
        }
      >
        <TextField
          select
          size="small"
          variant="standard"
          value={value}
          SelectProps={{
            onOpen: () => setMenuOpen(true),
            onClose: () => setMenuOpen(false),
          }}
          disabled={disabled || setTherapy.isPending || !therapyRxId}
          onChange={(e) =>
            setTherapy.mutate({
              therapyRxId: therapyRxId!,
              therapyId: e.target.value === "" ? null : e.target.value,
            })
          }
          sx={{ minWidth: 110, "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
        >
          <MenuItem value="">
            {defaultAbbreviation ? `Automatisch (${defaultAbbreviation})` : "Automatisch"}
          </MenuItem>
          {candidates.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.abbreviation}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>
    </Box>
  );
};

export default TherapyRxMethodCell;
